import { repeatItemInputFromData } from './repeat/itemMapper'
import { lockStateFor, pageKeysForLock } from './repeat/locks'
import type {
  RepeatItemInput,
  RepeatPageDoc,
  RepeatPagesInput,
  LockStateResult,
} from './repeat/types'
import {
  getPage,
  subscribeToPage,
} from './checklistBookService'
import { bookIdFor, type ChecklistType, type PageKey } from '../types/checklistBooks'

export interface LockTarget {
  itemId: string
  periodKey: string
}

export type LockGraph = Map<string, RepeatItemInput>

/**
 * buildLockGraph — pure helper to construct RepeatItemInput graph from all items
 */
export function buildLockGraph<T extends { id?: unknown }>(
  items: ReadonlyArray<T>,
): LockGraph {
  const graph = new Map<string, RepeatItemInput>()
  if (!Array.isArray(items)) return graph

  for (const item of items) {
    if (!item || typeof item !== 'object') continue
    const id = typeof item.id === 'string' ? item.id : ''
    if (!id) continue
    graph.set(id, repeatItemInputFromData(id, item as Record<string, unknown>))
  }

  return graph
}

/**
 * resolveLock — pure resolver returning lockStateFor result
 */
export function resolveLock(
  itemOrId: RepeatItemInput | string,
  periodKey: string,
  graph: LockGraph,
  pages?: RepeatPagesInput | null,
  now?: Date,
  timeZone?: string,
): LockStateResult {
  const item = typeof itemOrId === 'string' ? graph.get(itemOrId) : itemOrId
  if (!item) {
    return {
      isBlocked: true,
      isLocked: true,
      reasons: [{ code: 'DEPENDENCY_MISSING', targetId: typeof itemOrId === 'string' ? itemOrId : 'unknown' }],
      blockerCount: 1,
    }
  }

  let res: LockStateResult
  if (item.nodeKind === 'GATE_AND' || item.nodeKind === 'GATE_OR') {
    const dummyItem: RepeatItemInput = {
      id: `__gate_eval_${item.id}`,
      dependencyNodeIds: [item.id],
      checklistType: item.checklistType,
    }
    res = lockStateFor(dummyItem, periodKey, graph, pages, now, timeZone)
  } else {
    res = lockStateFor(item, periodKey, graph, pages, now, timeZone)
  }

  if (item.checklistType === 'DAILY' || item.checklistType === 'WEEKLY') {
    const windowEndIso = item.recurringWindow?.endDate ? item.recurringWindow.endDate.slice(0, 10) : null
    if (windowEndIso && periodKey > windowEndIso) {
      res.reasons.push({ code: 'WINDOW_ENDED' })
    }
  }

  for (const r of res.reasons) {
    if (r.missed && !r.missedPeriods) {
      r.missedPeriods = r.missed
    }
  }

  res.isLocked = res.isBlocked
  return res
}

/**
 * Collect unique page keys required across targets
 */
export function collectPageKeysForTargets(
  targets: LockTarget[],
  graph: LockGraph,
  now?: Date,
  timeZone?: string,
): Array<{ checklistType: 'DAILY' | 'WEEKLY'; pageKey: string }> {
  const result: Array<{ checklistType: 'DAILY' | 'WEEKLY'; pageKey: string }> = []
  const seen = new Set<string>()

  for (const target of targets) {
    const item = graph.get(target.itemId)
    if (!item) continue
    const needed = pageKeysForLock(item, target.periodKey, graph, now, timeZone)
    for (const entry of needed) {
      const key = `${entry.checklistType}:${entry.pageKey}`
      if (!seen.has(key)) {
        seen.add(key)
        result.push(entry)
      }
    }
  }

  return result
}

/**
 * loadLockPages — reads every needed page key exactly once through checklistBookService
 */
export async function loadLockPages(
  workspaceId: string,
  targets: LockTarget[],
  graph: LockGraph,
  now?: Date,
  timeZone?: string,
): Promise<Map<string, RepeatPageDoc>> {
  const pagesMap = new Map<string, RepeatPageDoc>()
  if (!workspaceId) return pagesMap

  const needed = collectPageKeysForTargets(targets, graph, now, timeZone)
  await Promise.all(
    needed.map(async ({ checklistType, pageKey }) => {
      try {
        const bookId = bookIdFor(workspaceId, checklistType as ChecklistType)
        const page = await getPage(bookId, pageKey as PageKey)
        if (page) {
          pagesMap.set(pageKey, page as RepeatPageDoc)
        }
      } catch (err) {
        console.warn(`[checklistLockService] failed to load page ${pageKey}:`, err)
      }
    }),
  )

  return pagesMap
}

/**
 * subscribeLockPages — subscribes to each needed page key for live screens
 */
export function subscribeLockPages(
  workspaceId: string,
  targets: LockTarget[],
  graph: LockGraph,
  onUpdate: (pages: Map<string, RepeatPageDoc>) => void,
  now?: Date,
  timeZone?: string,
): () => void {
  if (!workspaceId || targets.length === 0) {
    return () => {}
  }

  const needed = collectPageKeysForTargets(targets, graph, now, timeZone)
  const pagesMap = new Map<string, RepeatPageDoc>()
  const unsubs: Array<() => void> = []

  for (const { checklistType, pageKey } of needed) {
    const bookId = bookIdFor(workspaceId, checklistType as ChecklistType)
    const unsub = subscribeToPage(bookId, pageKey as PageKey, (page) => {
      if (page) {
        pagesMap.set(pageKey, page as RepeatPageDoc)
      } else {
        pagesMap.delete(pageKey)
      }
      onUpdate(new Map(pagesMap))
    })
    unsubs.push(unsub)
  }

  return () => {
    for (const unsub of unsubs) {
      try {
        unsub()
      } catch {
        // ignore unsubscribe errors
      }
    }
  }
}

